import { useMutation } from '@apollo/client'
import { useState } from 'react'
import { Button, NotificationType } from '../../../../components'
import { CLEAR_PORTFOLIO } from '../../../../gql'
import { Creator, MutationClearPortfolioArgs } from '../../../../gql/types'
import { useNotification } from '../../../../utils/hooks'
import { ReapplyIntroVideo } from './ReapplyIntroVideo'
import { ReapplyPortfolio } from './ReapplyPortfolio'
import { ReapplyReview } from './ReapplyReview'

interface Props {
  creator: Creator
  step: 0 | 1 | 2 | 3
  setStep: (step: 0 | 1 | 2 | 3) => void
}

const Reapplying = ({ creator, step, setStep }: Props) => {
  const { addNotification } = useNotification()
  const [applyAgainLoading, setApplyAgainLoading] = useState(false)

  const [clearPortfolio] = useMutation<
    { clearPortfolio: Creator },
    MutationClearPortfolioArgs
  >(CLEAR_PORTFOLIO)

  const onApplyAgain = async () => {
    setApplyAgainLoading(true)
    try {
      await clearPortfolio({
        variables: { creatorId: creator.id },
      })
      setStep(1)
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
    setApplyAgainLoading(false)
  }

  return (
    <div className='sm:mx-auto sm:w-full sm:max-w-2xl'>
      {step === 0 && (
        <div className='space-y-6 sm:p-6'>
          <div className='sm:mx-auto'>
            <h2 className='mt-0 text-left text-3xl font-extrabold text-gray-900'>
              Your application was not approved
            </h2>
            <p className='mt-4 text-left text-sm text-gray-600'>
              After a thorough review, we were unable to approve your
              application at this time. Your introduction video or portfolio did
              not meet our guidelines.
            </p>
            <p className='mt-4 text-left text-sm text-gray-600'>
              Before you apply again, take some time to study what we're looking
              for. Your content needs to match the quality and style of our
              approved creators.
            </p>
            <div className='mt-6 space-y-3'>
              <a
                href='https://useclip.com/our-ugc-creators'
                target='_blank'
                rel='noopener noreferrer'
                className='block p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors'
              >
                <p className='text-sm font-semibold text-gray-900'>
                  See our approved creators
                </p>
                <p className='mt-1 text-sm text-gray-500'>
                  Watch their intro videos to understand the quality and
                  presentation we expect.
                </p>
              </a>
              <a
                href='https://useclip.com/ugc-content'
                target='_blank'
                rel='noopener noreferrer'
                className='block p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors'
              >
                <p className='text-sm font-semibold text-gray-900'>
                  See examples of UGC content
                </p>
                <p className='mt-1 text-sm text-gray-500'>
                  Review the standard of portfolio videos your application will
                  be compared against.
                </p>
              </a>
            </div>
            <p className='mt-4 text-left text-sm text-gray-600'>
              Make sure your intro video and portfolio are as good as what you
              see above before reapplying.
            </p>
            {/* {(creator.resubmissionCount ?? 0) > 0 && (
              <p className='mt-2 text-xs text-gray-400'>
                {creator.resubmissionCount} of {creator.maxResubmissions}{' '}
                resubmissions used
              </p>
            )} */}
            <div className='mt-8 flex justify-center'>
              <Button
                type='button'
                title='Apply Again'
                onClick={onApplyAgain}
                loading={applyAgainLoading}
                disabled={applyAgainLoading}
                width='240px'
              />
            </div>
          </div>
        </div>
      )}

      <div style={{ display: step === 1 ? 'block' : 'none' }}>
        <ReapplyIntroVideo
          creator={creator}
          onContinue={() => setStep(2)}
          onBack={() => setStep(0)}
        />
      </div>

      <div style={{ display: step === 2 ? 'block' : 'none' }}>
        <ReapplyPortfolio
          creator={creator}
          onContinue={() => setStep(3)}
          onBack={() => setStep(1)}
        />
      </div>

      <div style={{ display: step === 3 ? 'block' : 'none' }}>
        <ReapplyReview creator={creator} onBack={() => setStep(2)} />
      </div>
    </div>
  )
}

export { Reapplying }
